import { CONFIG, RATINGS, api } from './_barrel.js';


        function SubjectBrief({ subject, incidents, assessments, reports, token, onGoToDetails, rubric }) {
            const activeAssessment = assessments.items.find(a => !a.superseded_at);
            const allInc = incidents.items || [];
            const activeInc = allInc.filter(i => !['closed','false_positive'].includes(i.status));

            const isHostile = (i) =>
                (i.llm_classification && i.llm_classification.startsWith('hostile_')) ||
                ['threat','doxxing','impersonation','disinformation'].includes(i.incident_type_code);

            const hostileActive = activeInc.filter(isHostile);
            const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
            const recent = allInc.filter(i => new Date(i.detected_at) >= cutoff);
            const recentHostile = recent.filter(isHostile);
            const breachActive = activeInc.filter(i => ['breach','breach_exposure','credential_breach','data_breach'].includes(i.incident_type_code));

            // Most recent analyst note across all incidents
            const incsWithNotes = allInc
                .filter(i => i.analyst_comment && i.analyst_comment_at)
                .sort((a, b) => new Date(b.analyst_comment_at) - new Date(a.analyst_comment_at));
            const lastNote = incsWithNotes[0] || null;

            // Last signed report
            const signedReports = (reports || []).filter(r => r.signed_off_at);
            const lastSigned = signedReports.length > 0 ? signedReports[0] : null;

            // Headline finding — reuse from last signed report if present, otherwise compute
            let headline = null;
            if (lastSigned && lastSigned.headline_finding) {
                headline = lastSigned.headline_finding;
            } else if (hostileActive.length === 0 && breachActive.length === 0) {
                headline = `The picture for ${subject.display_name || subject.full_name} is currently low. No active hostile incidents and no significant breach exposure on file.`;
            } else if (hostileActive.length > 0) {
                headline = `${hostileActive.length} active hostile incident${hostileActive.length === 1 ? '' : 's'} on file for ${subject.display_name || subject.full_name}. Review the threat picture before any planned public appearance or media engagement.`;
            } else {
                headline = `The picture for ${subject.display_name || subject.full_name} shows ${breachActive.length} active breach exposure incident${breachActive.length === 1 ? '' : 's'} but no current hostile activity. Vulnerability hardening is the priority.`;
            }

            // Top three live items
            const topThree = [];
            // Priority 1: critical or high hostile incidents
            const critHigh = hostileActive.filter(i => ['critical','high'].includes(i.severity)).slice(0, 2);
            critHigh.forEach(i => topThree.push({
                kind: 'hostile', severity: i.severity, label: i.title.slice(0, 120), date: i.detected_at,
            }));
            // Priority 2: recent hostile (last 30d) not already shown
            recentHostile.filter(i => !critHigh.includes(i)).slice(0, 3 - topThree.length).forEach(i => {
                topThree.push({ kind: 'recent', severity: i.severity, label: i.title.slice(0, 120), date: i.detected_at });
            });
            // Priority 3: active breach exposure
            if (topThree.length < 3 && breachActive.length > 0) {
                topThree.push({
                    kind: 'breach', severity: breachActive[0].severity,
                    label: `${breachActive.length} active breach exposure incident${breachActive.length === 1 ? '' : 's'} on file`,
                    date: breachActive[0].detected_at,
                });
            }
            // Priority 4: stale assessment if no other priorities
            if (topThree.length < 3 && activeAssessment) {
                const days = Math.floor((Date.now() - new Date(activeAssessment.created_at)) / (1000*60*60*24));
                if (days > 30) {
                    topThree.push({
                        kind: 'stale', severity: 'low',
                        label: `Active assessment is ${days} days old. Consider refreshing.`,
                        date: activeAssessment.created_at,
                    });
                }
            }
            if (topThree.length === 0) {
                topThree.push({
                    kind: 'clear', severity: 'low',
                    label: 'No active priorities. Routine monitoring only.',
                    date: new Date().toISOString(),
                });
            }

            const bandColour = activeAssessment ? RATINGS.hex(activeAssessment.risk_band) : 'var(--muted)';

            // Threat actors (danger-first) — computed once, reused by the panel and the printed one-pager.
            const _sevRank = { critical: 4, high: 3, moderate: 2, low: 1 };
            const topActors = (subject.prolific_clusters || [])
                .filter(c => (c.hostile_count || 0) > 0)
                .sort((a, b) => {
                    const sa = _sevRank[(a.max_hostile_severity||'').toLowerCase()] || 0;
                    const sb = _sevRank[(b.max_hostile_severity||'').toLowerCase()] || 0;
                    if (sb !== sa) return sb - sa;
                    return (b.hostile_count||0) - (a.hostile_count||0);
                })
                .slice(0, 5);

            // Print a clean, branded, single-page A4 brief. Generated as a
            // self-contained light-theme document (its own window) so it prints
            // to paper properly rather than screenshotting the dark UI. Uses the
            // canonical risk bands — ink-legible variants of the same hues.
            const printOnePager = () => {
                const PB = { low: '#2e7d46', moderate: '#9a7b1f', high: '#b9741d', critical: '#c0392b' };
                const band = activeAssessment ? String(activeAssessment.risk_band).toLowerCase() : null;
                const bandHex = band ? (PB[band] || '#555') : '#777';
                const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
                const dstr = (iso, o) => iso ? new Date(iso).toLocaleDateString('en-GB', o || {day:'numeric',month:'short',year:'numeric'}) : '';
                const rows = topThree.map((it, i) => `
                    <tr>
                      <td class="num">${i+1}</td>
                      <td>${esc(it.label)}<div class="meta">${esc((it.kind||'').replace(/_/g,' '))} · severity ${esc(it.severity)}</div></td>
                    </tr>`).join('');
                const actorRows = topActors.length ? topActors.map((a, i) => `
                    <tr>
                      <td class="num">${i+1}</td>
                      <td>@${esc(a.source_account)}<div class="meta">${esc((a.max_hostile_severity||'').toUpperCase())} · ${esc(a.hostile_count)} hostile${a.hostile_last_seen ? ' · last ' + esc(dstr(a.hostile_last_seen,{day:'numeric',month:'short'})) : ''}</div></td>
                    </tr>`).join('') : '<tr><td colspan="2" class="meta" style="padding:6px 0">No attributed threat actors on file.</td></tr>';
                const who = esc(subject.display_name || subject.full_name);
                const html = `<!doctype html><html><head><meta charset="utf-8"><title>Brief — ${who}</title>
                <style>
                  @page { size: A4; margin: 14mm; }
                  * { box-sizing: border-box; }
                  body { font-family: Georgia, 'Times New Roman', serif; color: #1a1a1a; margin: 0; line-height: 1.4; font-size: 11pt; }
                  .mono { font-family: 'Courier New', monospace; }
                  .eyebrow { font-family: 'Courier New', monospace; text-transform: uppercase; letter-spacing: 0.12em; font-size: 7.5pt; color: #777; }
                  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #c9a227; padding-bottom: 10px; margin-bottom: 16px; }
                  h1 { font-size: 20pt; margin: 2px 0 0; letter-spacing: 0.02em; }
                  .sub { color: #555; font-size: 10pt; margin-top: 3px; }
                  .brandname { font-family: 'Courier New', monospace; letter-spacing: 0.18em; font-size: 11pt; color: #c9a227; font-weight: bold; }
                  .bandbox { text-align: right; border: 1.5px solid ${bandHex}; padding: 8px 12px; min-width: 130px; }
                  .bandval { font-size: 20pt; font-weight: bold; color: ${bandHex}; line-height: 1; }
                  .sec { margin: 14px 0; }
                  .sec h2 { font-family: 'Courier New', monospace; text-transform: uppercase; letter-spacing: 0.1em; font-size: 8.5pt; color: #777; border-bottom: 1px solid #ddd; padding-bottom: 4px; margin: 0 0 8px; }
                  .headline { font-size: 13pt; line-height: 1.45; border-left: 3px solid #c9a227; padding-left: 12px; }
                  table { width: 100%; border-collapse: collapse; }
                  td { padding: 5px 0; vertical-align: top; border-bottom: 1px solid #eee; }
                  td.num { width: 22px; color: #c9a227; font-weight: bold; font-family: 'Courier New', monospace; }
                  .meta { font-family: 'Courier New', monospace; font-size: 8pt; color: #888; margin-top: 2px; }
                  .two { display: flex; gap: 20px; }
                  .two > div { flex: 1; }
                  .note { font-size: 10pt; color: #333; }
                  footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; font-family: 'Courier New', monospace; font-size: 7.5pt; color: #999; display: flex; justify-content: space-between; }
                  .restricted { color: #c0392b; font-weight: bold; }
                </style></head><body>
                  <header>
                    <div>
                      <div class="brandname">OBSIDIAN VEIL</div>
                      <div class="eyebrow" style="margin-top:6px">Protective Intelligence Brief</div>
                      <h1>${who}</h1>
                      <div class="sub">${esc(subject.full_name)}${subject.occupation ? ' · ' + esc(subject.occupation) : ''}${subject.current_employer ? ' · ' + esc(subject.current_employer) : ''}</div>
                      ${subject.is_minor ? '<div class="sub restricted">MINOR · RESTRICTED HANDLING</div>' : ''}
                    </div>
                    <div class="bandbox">
                      <div class="eyebrow">Current Risk Band</div>
                      <div class="bandval">${band ? band.toUpperCase() : 'NOT SET'}</div>
                      <div class="mono" style="font-size:8pt;color:#666;margin-top:3px">${activeAssessment ? 'Composite ' + Number(activeAssessment.composite_score).toFixed(1) : 'No assessment'}</div>
                    </div>
                  </header>
                  <div class="sec"><h2>Headline</h2><div class="headline">${esc(headline)}</div></div>
                  <div class="sec"><h2>What you need to know</h2><table>${rows}</table></div>
                  <div class="two">
                    <div class="sec"><h2>Top threat actors</h2><table>${actorRows}</table></div>
                    <div class="sec"><h2>Last analyst note</h2><div class="note">${lastNote ? esc(lastNote.analyst_comment) + '<div class="meta">' + esc(dstr(lastNote.analyst_comment_at)) + '</div>' : '<span class="meta">No analyst notes on file.</span>'}</div>
                      ${lastSigned ? '<div style="margin-top:10px"><h2>Last signed report</h2><div class="note mono" style="font-size:9pt">' + esc(lastSigned.report_number || '') + ' · ' + esc(dstr(lastSigned.signed_off_at)) + '</div></div>' : ''}
                    </div>
                  </div>
                  <footer>
                    <span>Obsidian Arc Ltd · Protective Intelligence · CONFIDENTIAL</span>
                    <span>Generated ${esc(dstr(new Date().toISOString(), {day:'numeric',month:'short',year:'numeric'}))} · ${esc(subject.subject_type_code || '')}</span>
                  </footer>
                  <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
                </body></html>`;
                const w = window.open('', '_blank');
                if (!w) { alert('Please allow pop-ups to print the one-pager.'); return; }
                w.document.open(); w.document.write(html); w.document.close();
            };

            const photoUrl = subject.photo_uploaded_at && token
                ? `${CONFIG.apiBase}/api/v1/subjects/${subject.id}/photo?access_token=${encodeURIComponent(token)}`
                : null;

            return (
                <div className="space-y-6">
                    {/* Brief toolbar */}
                    <div className="flex justify-between items-center">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted">Protective Intelligence Brief</div>
                        <button className="btn-ghost" onClick={printOnePager}
                                title="Open a clean, branded one-page A4 brief in a new tab, ready to print or save as PDF for a principal, driver or client.">
                            🖨 Print one-pager
                        </button>
                    </div>
                    {/* Top row: photo + identity | risk band */}
                    <div className="grid grid-cols-3 gap-6">
                        <div className="panel p-6 col-span-2">
                            <div className="flex gap-5 items-start">
                                {photoUrl ? (
                                    <img
                                        src={photoUrl}
                                        alt=""
                                        style={{
                                            width: '120px', height: '120px', objectFit: 'cover',
                                            border: '1px solid var(--gold)', flexShrink: 0,
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        width: '120px', height: '120px',
                                        border: '1px dashed var(--muted)', flexShrink: 0,
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                    }} className="text-xs text-muted font-mono">no photo</div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">Subject</div>
                                    <div className="font-display text-3xl gold mb-2">{subject.display_name || subject.full_name}</div>
                                    <div className="text-sm text-muted">{subject.full_name}</div>
                                    {subject.occupation && (
                                        <div className="text-sm mt-2">{subject.occupation}</div>
                                    )}
                                    {subject.current_employer && (
                                        <div className="text-xs text-muted">{subject.current_employer}</div>
                                    )}
                                    {(subject.known_locations || []).length > 0 && (
                                        <div className="text-xs text-muted mt-1">
                                            {subject.known_locations.join(' · ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="panel p-6 col-span-1" style={{borderColor: bandColour}}>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Current Risk Band</div>
                            <div className="font-display text-5xl" style={{color: bandColour, lineHeight: 1.1}}>
                                {activeAssessment ? activeAssessment.risk_band.toUpperCase() : 'NOT SET'}
                            </div>
                            <div className="text-sm text-muted mt-2 font-mono">
                                {activeAssessment ? `Composite ${Number(activeAssessment.composite_score).toFixed(1)}` : 'No assessment on file'}
                            </div>
                            {activeAssessment && (
                                <div className="text-xs text-muted font-mono mt-1">
                                    Set {new Date(activeAssessment.created_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top threat actors — danger-first, matches Watched Accounts panel */}
                    {(() => {
                        const _sevRank = { critical: 4, high: 3, moderate: 2, low: 1 };
                        const actors = (subject.prolific_clusters || [])
                            .filter(c => (c.hostile_count || 0) > 0)
                            .sort((a, b) => {
                                const sa = _sevRank[(a.max_hostile_severity||'').toLowerCase()] || 0;
                                const sb = _sevRank[(b.max_hostile_severity||'').toLowerCase()] || 0;
                                if (sb !== sa) return sb - sa;
                                return (b.hostile_count||0) - (a.hostile_count||0);
                            })
                            .slice(0, 5);
                        if (actors.length === 0) return null;
                        const sevColour = (s) => RATINGS.hex(s);   // canonical band palette
                        return (
                            <div className="panel p-6">
                                <div className="font-mono text-xs uppercase tracking-widest text-muted mb-4">Top Threat Actors</div>
                                <div className="space-y-3">
                                    {actors.map((a, idx) => (
                                        <div key={a.source_account} className="flex gap-4 items-center">
                                            <div className="font-display text-xl" style={{color: sevColour(a.max_hostile_severity), flexShrink: 0, width: '24px'}}>{idx + 1}</div>
                                            <div className="flex-1">
                                                <div className="text-sm">@{a.source_account}</div>
                                                <div className="text-xs text-muted font-mono mt-1">
                                                    <span style={{color: sevColour(a.max_hostile_severity), fontWeight: 600}}>{(a.max_hostile_severity||'').toUpperCase()}</span>
                                                    {' · '}{a.hostile_count} hostile{a.hostile_last_seen ? ' · last ' + new Date(a.hostile_last_seen).toLocaleDateString('en-GB', {day:'numeric', month:'short'}) : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                    {/* Headline finding */}
                    <div className="panel p-6" style={{borderLeft: '3px solid var(--gold)'}}>
                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Headline</div>
                        <div className="font-display text-xl" style={{lineHeight: 1.4}}>{headline}</div>
                        {lastSigned && lastSigned.headline_finding && (
                            <div className="text-xs text-muted font-mono mt-3">
                                From signed report {lastSigned.report_number} · {new Date(lastSigned.signed_off_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                            </div>
                        )}
                    </div>

                    {/* Top three live items */}
                    <div className="panel p-6">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-4">What You Need To Know</div>
                        <div className="space-y-3">
                            {topThree.map((item, idx) => (
                                <div key={idx} className="flex gap-4 items-start">
                                    <div className="font-display text-xl" style={{color: 'var(--gold)', flexShrink: 0, width: '24px'}}>{idx + 1}</div>
                                    <div className="flex-1">
                                        <div className="text-sm">{item.label}</div>
                                        <div className="text-xs text-muted font-mono mt-1">
                                            {item.kind === 'hostile' && 'Active hostile incident · '}
                                            {item.kind === 'recent' && 'Recent (last 30d) · '}
                                            {item.kind === 'breach' && 'Breach exposure · '}
                                            {item.kind === 'stale' && 'Operational · '}
                                            {item.kind === 'clear' && 'Status · '}
                                            severity {item.severity}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Last analyst note + last signed report */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="panel p-6">
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Last Analyst Note</div>
                            {lastNote ? (
                                <div>
                                    <div className="text-xs text-muted font-mono mb-2">
                                        On: {lastNote.title.slice(0, 80)}{lastNote.title.length > 80 ? '…' : ''}
                                    </div>
                                    <div className="text-sm" style={{lineHeight: 1.5}}>{lastNote.analyst_comment}</div>
                                    <div className="text-xs text-muted font-mono mt-3">
                                        {new Date(lastNote.analyst_comment_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-muted">No analyst notes on this subject yet.</div>
                            )}
                        </div>
                        <div className="panel p-6">
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Last Signed Report</div>
                            {lastSigned ? (
                                <div>
                                    <div className="font-mono text-sm gold mb-1">{lastSigned.report_number}</div>
                                    <div className="text-xs text-muted">
                                        Signed by {lastSigned.signed_off_by_display_name}
                                        {lastSigned.signed_off_by_role ? `, ${lastSigned.signed_off_by_role}` : ''}
                                    </div>
                                    <div className="text-xs text-muted font-mono mt-2">
                                        {new Date(lastSigned.signed_off_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-muted">No signed reports yet. Open the Details tab to generate one.</div>
                            )}
                        </div>
                    </div>

                    <div className="text-center pt-2">
                        <button className="btn-ghost" onClick={onGoToDetails}>
                            Open full details →
                        </button>
                    </div>
                </div>
            );
        }

export { SubjectBrief };
